import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentsApi } from '../services/api';
import { AuthShell } from '../components/auth/AuthField';
import { Doodle, WritingScribble } from '../components/paper';

const RETRY_DELAY_MS = 2000;
const MAX_ATTEMPTS = 3;

export const SubscriptionSuccess: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (user?.is_subscribed) {
      navigate('/dashboard', { replace: true });
      return;
    }

    let cancelled = false;

    // Ask the backend to read the subscription straight from Paddle rather than
    // polling /me until the webhook happens to land. The retries cover the window
    // before Paddle's subscription.created webhook has linked the subscription.
    const confirm = async () => {
      for (let attempt = 0; attempt < MAX_ATTEMPTS && !cancelled; attempt += 1) {
        try {
          const status = await paymentsApi.sync();
          if (cancelled) return;
          if (status.is_active) {
            await refreshUser();
            return;
          }
        } catch {
          // Fall through to the retry; the copy below covers a persistent failure.
        }
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      }
      if (!cancelled) setStalled(true);
    };

    confirm();
    return () => {
      cancelled = true;
    };
  }, [user?.is_subscribed, refreshUser, navigate]);

  return (
    <AuthShell>
      <div className="flex flex-col items-center pt-12 text-center">
        <Doodle kind="page" />
        <WritingScribble width={140} className="mt-6" />
        <h1 className="pb-heading-2 m-0 mt-4">Finishing up your subscription…</h1>
        <p className="m-0 mt-2 max-w-[20rem] font-body text-[0.9375rem] text-on-surface-variant">
          Paddle is confirming your payment. This usually takes a few seconds.
        </p>
        {stalled && (
          <p className="m-0 mt-4 font-body text-[0.8125rem] text-on-surface-variant">
            Still working on it — you can{' '}
            <button
              type="button"
              className="cursor-pointer border-none bg-transparent p-0 font-body text-[0.8125rem] font-semibold text-accent hover:underline"
              onClick={() => navigate('/subscription', { replace: true })}
            >
              check your subscription
            </button>{' '}
            or refresh this page.
          </p>
        )}
      </div>
    </AuthShell>
  );
};
